/* A one-bit shared signal: "a full-screen overlay owns the viewport right now".

   The only consumer today is the chat launcher (components/Chatbot.tsx), which
   is sitewide chrome mounted in app/layout.tsx and floats at z-[70] — ABOVE the
   mobile menu (z-40/z-50 in components/Header.tsx). Without this the launcher
   would sit on top of the open burger menu, over the drawer's own controls.

   Why a module store and not a DOM attribute or an event: the drawer is
   rendered by <Header />, the launcher is its SIBLING in the layout, so there
   is no shared React state to lift into. useSyncExternalStore is the pattern
   chatbot.tsx already uses for its sessionStorage-backed thread, and it is the
   only safe way to read out-of-React state on a static export — reading in a
   useState initializer would desync from the pre-rendered HTML and reading in
   an effect cascades a second render.

   Reference-counted rather than a boolean: two overlays could conceivably be
   open at once (the drawer over a modal), and a plain flag would let
   whichever closed FIRST un-hide the launcher underneath the other. The
   Header acquires it while its mobile menu is open. */

let count = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

/** Mark one overlay open. Returns the matching release — call it on unmount. */
export function acquireOverlay(): () => void {
  count += 1;
  emit();
  let released = false;
  return () => {
    /* Guard double-release: React StrictMode runs effect cleanups twice in
       development, which would otherwise drive the count negative. */
    if (released) return;
    released = true;
    count -= 1;
    emit();
  };
}

export function subscribeToOverlay(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

export function readOverlayOpen(): boolean {
  return count > 0;
}

/** Server snapshot: nothing is open during the static render. */
export function readOverlayOpenServer(): boolean {
  return false;
}
